import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api, mutate } from '../../shared/api';
import { formatMoney } from '../../shared/formatting';
import type { User } from '../../types/biApp';

type Locale = 'hr' | 'en';

const BROWN = 'var(--ad-ink)';
const GOLD = 'var(--ad-acc)';

const card: React.CSSProperties = { background: '#fff', borderRadius: 14, padding: 18, boxShadow: '0 2px 10px rgba(107,79,28,0.10)', border: '1px solid #efe6d2' };
const inp: React.CSSProperties = { padding: '8px 10px', borderRadius: 8, border: '1px solid #d8cdb2', fontSize: '0.86rem', width: '100%' };
const th: React.CSSProperties = { textAlign: 'left', padding: '8px 10px', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: BROWN, borderBottom: '2px solid #efe6d2', whiteSpace: 'nowrap' };
const td: React.CSSProperties = { padding: '8px 10px', fontSize: '0.82rem', borderBottom: '1px solid #f3ecdb', whiteSpace: 'nowrap' };
const tdNum: React.CSSProperties = { ...td, textAlign: 'right' };

type Row = {
    id: number;
    employee_name: string | null; employee_oib: string | null;
    company_id: number | null; fiscal_year: number; fiscal_month: number;
    department_id: number | null; department_name: string | null; region: string | null; branch_name: string | null;
    buyback_turnover: number; exchange_turnover: number; investment_turnover: number; pawn_turnover: number;
    total_turnover: number; total_ruc: number; total_revenue: number; total_stim: number;
    plan_target: number; plan_actual: number; plan_pct: number; plan_stim: number;
    coefficient: number; adjusted_stim: number; gross1: number; net_approx: number;
    employee_id: number | null;
    branch_assignment_id: number | null;
    plaza_coefficient: number | null;
    coefficient_locked: number | null;
    branch_promet: number | null;
};

const MONTHS_HR = ['', 'Siječanj', 'Veljača', 'Ožujak', 'Travanj', 'Svibanj', 'Lipanj', 'Srpanj', 'Kolovoz', 'Rujan', 'Listopad', 'Studeni', 'Prosinac'];

export function StimulacijeTab({ token, locale }: { token: string; locale: Locale; user: User }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const eur = (n: number | null) => (n === null || n === undefined ? '—' : formatMoney(Number(n) || 0, locale));

    const now = new Date();
    const [year, setYear] = useState<number>(now.getFullYear());
    const [month, setMonth] = useState<number>(now.getMonth() + 1);
    const [rows, setRows] = useState<Row[] | null>(null);
    const [canEdit, setCanEdit] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const [editId, setEditId] = useState<number | null>(null);
    const [editVal, setEditVal] = useState('');
    const [savingId, setSavingId] = useState<number | null>(null);

    const load = useCallback(() => {
        setRows(null); setErr(null);
        api<{ rows: Row[]; can_edit: boolean }>(`/hr/stimulacije?fiscal_year=${year}&fiscal_month=${month}`, token)
            .then((r) => { setRows(r.rows); setCanEdit(r.can_edit); })
            .catch((e) => { setErr(String(e?.message ?? e)); setRows([]); });
    }, [token, year, month]);
    useEffect(load, [load]);

    const startEdit = (r: Row) => { if (!r.branch_assignment_id) return; setEditId(r.branch_assignment_id); setEditVal(String(r.plaza_coefficient ?? '')); };
    const saveEdit = (assignmentId: number) => {
        setSavingId(assignmentId); setErr(null);
        mutate(`/hr/branch-assignments/${assignmentId}/coefficient`, token, 'PATCH', { plaza_coefficient: Number(editVal) })
            .then(() => { setEditId(null); load(); })
            .catch((e) => setErr(String(e?.message ?? e)))
            .finally(() => setSavingId(null));
    };

    const years = useMemo(() => { const y = now.getFullYear(); return [y + 1, y, y - 1, y - 2]; }, []);

    return (
        <div>
            <div style={{ background: '#fdf3e0', border: '1px solid ' + GOLD, borderRadius: 10, padding: '10px 14px', marginBottom: 14, color: 'var(--ad-acc-3)', fontSize: '0.82rem' }}>
                {t('Koeficijent se može ručno zaključati 🔒 — re-import stimulacija ga tada NE pregazi.', 'The coefficient can be manually locked 🔒 — a stimulation re-import will then NOT overwrite it.')}
            </div>

            <div style={{ ...card, marginBottom: 14, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
                <label style={{ flex: '1 1 120px' }}>{t('Godina', 'Year')}
                    <select style={inp} value={year} onChange={(e) => setYear(Number(e.target.value))}>
                        {years.map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>
                </label>
                <label style={{ flex: '1 1 150px' }}>{t('Mjesec', 'Month')}
                    <select style={inp} value={month} onChange={(e) => setMonth(Number(e.target.value))}>
                        {MONTHS_HR.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
                    </select>
                </label>
            </div>

            {err ? <p className="error" style={{ fontSize: '0.84rem' }}>{err}</p> : null}

            <div style={card}>
                <strong style={{ color: BROWN }}>{t('Stimulacije', 'Incentives')} — {MONTHS_HR[month]} {year} {rows ? `(${rows.length})` : ''}</strong>
                {rows === null ? <p className="muted">{t('Učitavam…', 'Loading…')}</p>
                    : rows.length === 0 ? <p className="muted" style={{ fontStyle: 'italic' }}>{t('Nema stimulacijskih zapisa za ovo razdoblje.', 'No incentive records for this period.')}</p>
                        : (
                            <div style={{ overflowX: 'auto', marginTop: 10 }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead><tr>
                                        <th style={th}>{t('Djelatnik', 'Employee')}</th>
                                        <th style={th}>{t('Poslovnica', 'Branch')}</th>
                                        <th style={{ ...th, textAlign: 'right' }}>{t('Otkup', 'Buyback')}</th>
                                        <th style={{ ...th, textAlign: 'right' }}>{t('Mjenjač', 'Exchange')}</th>
                                        <th style={{ ...th, textAlign: 'right' }}>{t('Invest', 'Invest')}</th>
                                        <th style={{ ...th, textAlign: 'right' }}>{t('Zalog', 'Pawn')}</th>
                                        <th style={{ ...th, textAlign: 'right' }}>{t('Promet uk.', 'Turnover')}</th>
                                        <th style={{ ...th, textAlign: 'right' }}>{t('Promet (JSON)', 'Promet (JSON)')}</th>
                                        <th style={{ ...th, textAlign: 'right' }}>RUC</th>
                                        <th style={{ ...th, textAlign: 'right' }}>{t('Stimulacija', 'Incentive')}</th>
                                        <th style={{ ...th, textAlign: 'right' }}>{t('Koeficijent', 'Coefficient')}</th>
                                    </tr></thead>
                                    <tbody>
                                        {rows.map((r) => (
                                            <tr key={r.id}>
                                                <td style={td}>{r.employee_name ?? '—'}</td>
                                                <td style={td}>{r.department_name ?? r.branch_name ?? '—'}</td>
                                                <td style={tdNum}>{eur(r.buyback_turnover)}</td>
                                                <td style={tdNum}>{eur(r.exchange_turnover)}</td>
                                                <td style={tdNum}>{eur(r.investment_turnover)}</td>
                                                <td style={tdNum}>{eur(r.pawn_turnover)}</td>
                                                <td style={{ ...tdNum, fontWeight: 600 }}>{eur(r.total_turnover)}</td>
                                                <td style={tdNum}>{eur(r.branch_promet)}</td>
                                                <td style={tdNum}>{eur(r.total_ruc)}</td>
                                                <td style={{ ...tdNum, fontWeight: 700 }}>{eur(r.adjusted_stim || r.total_stim)}</td>
                                                <td style={tdNum}>
                                                    {editId === r.branch_assignment_id && r.branch_assignment_id ? (
                                                        <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                                                            <input style={{ ...inp, width: 70, padding: '4px 6px' }} type="number" step="0.01" value={editVal} onChange={(e) => setEditVal(e.target.value)} />
                                                            <button type="button" style={{ border: 'none', background: BROWN, color: '#fff', borderRadius: 6, padding: '4px 8px', cursor: 'pointer' }} disabled={savingId === r.branch_assignment_id} onClick={() => saveEdit(r.branch_assignment_id!)}>✓</button>
                                                            <button type="button" style={{ border: '1px solid #e0d6c0', background: '#fff', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', color: BROWN }} onClick={() => setEditId(null)}>✕</button>
                                                        </span>
                                                    ) : (
                                                        <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', justifyContent: 'flex-end' }}>
                                                            {r.branch_assignment_id ? (
                                                                <>
                                                                    {(r.plaza_coefficient ?? 0).toFixed(2)}
                                                                    {r.coefficient_locked ? <span title={t('Zaključano', 'Locked')}>🔒</span> : null}
                                                                    {canEdit ? <button type="button" style={{ border: 'none', background: 'none', cursor: 'pointer', color: GOLD, fontSize: '0.9rem' }} title={t('Uredi koeficijent', 'Edit coefficient')} onClick={() => startEdit(r)}>✏️</button> : null}
                                                                </>
                                                            ) : <span className="muted">{(r.coefficient ?? 0).toFixed(2)}</span>}
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
            </div>
        </div>
    );
}
